import { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import MessageBox from '../../MessageBox/MessageBox';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { isAxiosError } from 'axios';
import axios from '../../../api/axios';
import './MemberSegment.css';

export interface IMemberInput {
  firstName: string;
  lastName: string;
  instrument: string;
  microphone: string;
  phone: string;
}

export interface IMemberWrapper {
  id: number;
  inDB: boolean;
  action: 'add' | 'update' | 'delete' | 'none';
  input: IMemberInput;
}

interface props {
  data: IMemberWrapper[];
}

const PHONE_REGEX = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{3,6}$/;

const SUBMIT_BUTTON_TITLE = 'ZAPISZ CZŁONKÓW ZESPOŁU';

const MemberSegment = (props: props) => {
  const axiosPrivate = useAxiosPrivate();
  const bandId = sessionStorage.getItem('editedBandId');

  const MEMBER_URL = bandId ? `/admin/bandMembers/${bandId}` : '/bandMembers';
  const SUCCESS_MESSAGE = 'Dane zostały poprawnie zapisane!';
  const ERROR_MESSAGE = 'Podczas zapisu danych wystąpił błąd!';
  const EDIT_ERROR = 'Po wysłaniu zgłoszenia nie można go edytować!';

  const [memberList, setMemberList] = useState<IMemberWrapper[]>([]);
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState<IMemberWrapper>();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IMemberInput>({
    defaultValues: {
      firstName: '',
      lastName: '',
      instrument: '',
      microphone: '',
      phone: '',
    },
  });

  useEffect(() => {
    setMemberList(props.data);
    sessionStorage.setItem('isMember', props.data.length > 0 ? '1' : '0');
  }, [props]);

  const onSubmit: SubmitHandler<IMemberInput> = (data) => {
    const isDuplicate = memberList.some(
      (member) =>
        member.input.firstName.toLowerCase() === data.firstName.toLowerCase() &&
        member.input.lastName.toLowerCase() === data.lastName.toLowerCase() &&
        member.action !== 'delete' &&
        (!edit || member !== selected)
    );
  
    if (isDuplicate) {
      setResult(false);
      setErrorMsg('Członek o takim imieniu i nazwisku już istnieje!');
      setOpen(true);
      return;
    }
  
    let updatedList;
  
    if (edit) {
      if (selected) {
        selected.input = data;
        selected.action = selected.inDB ? 'update' : 'add';
      }
      setEdit(false);
      updatedList = [...memberList];
    } else {
      updatedList = [
        ...memberList,
        {
          id: 0,
          inDB: false,
          action: 'add' as 'add',
          input: data,
        },
      ];
    }
  
    setMemberList(updatedList);
  
    sessionStorage.setItem('isMember', '1');
  
    reset();
  };

  const GenerateList = () => (
    <>
      {memberList
        .filter((m) => m.action !== 'delete')
        .map((member) => (
          <ListItem
            key={memberList.indexOf(member)}
            disablePadding
            secondaryAction={
              <IconButton onClick={() => removeMember(member)}>
                <DeleteIcon style={{ color: '#ffffff' }} />
              </IconButton>
            }
          >
            <ListItemButton
              selected={edit && member === selected}
              onClick={() => selectMember(member)}
            >
              <ListItemText
                primary={`${member.input.firstName} ${member.input.lastName}`}
                style={{ color: '#ffffff' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
    </>
  );

  const removeMember = (member: IMemberWrapper) => {
    const updatedList = memberList.map((m) =>
      m !== member ? m : { ...m, action: 'delete' as 'delete' }
    );
  
    setMemberList(updatedList);
  
    const isMember = updatedList.some(m => m.action !== 'delete');
    sessionStorage.setItem('isMember', isMember ? '1' : '0');
  
    if (member === selected) {
      reset();
      setEdit(false);
    }
  };

  const selectMember = (member: IMemberWrapper) => {
    if (edit && member === selected) {
      reset();
      setEdit(false);
      return;
    }

    setValue('firstName', member.input.firstName);
    setValue('lastName', member.input.lastName);
    setValue('instrument', member.input.instrument);
    setValue('microphone', member.input.microphone);
    setValue('phone', member.input.phone);

    setSelected(member);
    setEdit(true);
  };

  const submitList = async () => {
    console.log("Przed wysyłką:", memberList);

    try {
        const response = await axiosPrivate.post(MEMBER_URL, JSON.stringify(memberList), {
            headers: { 'Content-Type': 'application/json' },
        });

        console.log("Odpowiedź z serwera:", response.data);

        const newIds: number[] = response.data;
        let idIndex = 0;

        const updatedList = memberList.map(member => {
            if (member.action === "add" && idIndex < newIds.length) {
                return { ...member, id: newIds[idIndex++], inDB: true, action: 'none' as 'none' };
            }
            return member;
        });

        setMemberList(updatedList);
        setResult(true);
        setErrorMsg(SUCCESS_MESSAGE);

    } catch (error) {
        console.error("Błąd podczas wysyłania listy:", error);

        if (isAxiosError(error) && error.response?.status === 400) {
            setErrorMsg(SUCCESS_MESSAGE);
            setResult(true);
        } else if (isAxiosError(error) && error.response?.status === 420) {
            setErrorMsg(EDIT_ERROR);
            setResult(false);
        } else {
            setErrorMsg(ERROR_MESSAGE);
            setResult(false);
        }
    } finally {
        setOpen(true);
    }
};


  return (
    <div className="member-container">
      <h2>Członkowie Zespołu</h2>
      <form className="member-content" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Imię</label>
              <TextField {...field} error={errors.firstName ? true : false} helperText={errors.firstName ? 'Imię jest wymagane!' : ''} />
            </div>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Nazwisko</label>
              <TextField {...field} error={errors.lastName ? true : false} helperText={errors.lastName ? 'Nazwisko jest wymagane!' : ''} />
            </div>
          )}
        />
        <Controller
          name="instrument"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Instrument</label>
              <TextField {...field} error={errors.instrument ? true : false} helperText={errors.instrument ? 'Instrument jest wymagany!' : ''} />
            </div>
          )}
        />
        <Controller
          name="microphone"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Mikrofon / Linia</label>
              <TextField {...field} error={errors.microphone ? true : false} helperText={errors.microphone ? 'Mikrofon / linia jest wymagane!' : ''} />
            </div>
          )}
        />
        <Controller
          name="phone"
          control={control}
          rules={{
            validate: {
              isPhonePresent: (value) => {
                const isValidPhoneInList = memberList.some((member) =>
                  PHONE_REGEX.test(member.input.phone)
                );
                const isCurrentPhoneValid = PHONE_REGEX.test(value);

                return (
                  isValidPhoneInList || isCurrentPhoneValid || 'Przynajmniej 1 numer telefonu jest wymagany!'
                );
              },
              isPhoneValid: (value) =>
                value === '' || PHONE_REGEX.test(value) || 'Nieprawidłowy format numeru telefonu!',
            },
          }}
          render={({ field }) => (
            <div>
              <label>Telefon</label>
              <TextField
                {...field}
                error={errors.phone ? true : false}
                helperText={errors.phone?.message}
                placeholder='123 456 789'
              />
            </div>
          )}
        />
        <Button
          variant="contained"
          type="submit"
          sx={{ borderRadius: '0', marginTop: '10px' }}
        >
          {edit ? <EditIcon /> : <PersonAddIcon />}
        </Button>
        <List className="member-list" disablePadding>
          <GenerateList />
        </List>
      </form>
      <Button variant="contained" size="large" onClick={submitList}>
        {SUBMIT_BUTTON_TITLE}
      </Button>
      <MessageBox open={open} setOpen={setOpen} error={!result} message={errorMsg} />
    </div>
  );
};

export default MemberSegment;
